import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, DatePicker, Form, message, Modal, Select, Space, Switch, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { productsSettingsActions } from "@fitness-app/app-store";

import {
  ProductChargeType,
  ProductStatus,
  ProductType,
  type ProductPrice,
  type ProductTypeEnum,
} from "@fitness-app/data-models/entities/Product";
import { type ProductClient } from "@fitness-app/data-models/entities/ProductClient";
import {
  ClientImportAggregation,
  ImportDuplicatePolicy,
  type GrantProductAccessRequest,
  type GrantProductAccessResponse,
  type ImportAccessSource,
} from "@fitness-app/data-models/entities/ProductClientToImport";
import { getErrorMessage } from "@fitness-app/utils";
import { getLinkedSourceAccessEnd } from "@fitness-app/utils/src/products/getLinkedSourceAccessEnd";

import {
  DEFAULT_STRIPE_ACCOUNT_KEY,
  getProductStripeAccountKey,
  getStripeAccountLabel,
} from "~/modules/Products/helpers/productStripeAccount";
import { getProductPrice } from "~/modules/Products/hooks/useProductTaskAutomation";
import {
  decodeAccessSource,
  encodeAccessSource,
} from "~/modules/Products/Product/ProductClientsList/components/ImportClientsModal/accessSource";
import { ImportSummaryCounters } from "~/modules/Products/Product/ProductClientsList/components/ProductImports/ImportSummaryCounters";
import { useProductImport } from "~/modules/Products/Product/ProductClientsList/components/ProductImports/useProductImports";
import { usePaymentGatewayOptions } from "~/modules/Products/ProductForm/hooks/usePaymentGatewayOptions";
import { productApi, useAppDispatch, useAppSelector } from "~/store/initializeStore";

interface FormModel {
  productId: string;
  /** Encoded `AccessSourceValue`. */
  accessSource: string;
  accessTo: Dayjs | null;
  followSource: boolean;
  sendEmailAboutAccountCreation: boolean;
}

interface Props {
  customer: { email: string; firstName: string | null; lastName: string | null; phoneNumber: string | null } | null;
  /** The customer's rows across products — the "like in product X" options come from the active ones. */
  customerProducts: ProductClient[];
  open: boolean;
  onClose: () => void;
  onScheduled?: () => void;
}

/** Order of the product groups in the select; the type is the first thing an admin knows about a product. */
const PRODUCT_TYPE_GROUPS: { type: ProductTypeEnum; key: string }[] = [
  { type: ProductType.PERSONAL_CLIENT_SERVICE, key: "personalClientService" },
  { type: ProductType.AUTOMATED_CLIENT_SERVICE, key: "automatedClientService" },
  { type: ProductType.ADDON, key: "addon" },
  { type: ProductType.ONLY_CONTENT, key: "onlyContent" },
  { type: ProductType.EVENT, key: "event" },
];

const priceLabel = (price: ProductPrice, t: (key: string, options?: Record<string, unknown>) => string) =>
  `${t(`products:paymentTypeOptions.${price.type}`)}${
    price.accessPeriod
      ? ` (${t(`products:${price.type}.${price.accessPeriod.interval}`, { count: price.accessPeriod.interval_count })})`
      : ""
  } - ${getProductPrice(price)}${price.customLabel ? ` - ${price.customLabel}` : ""}`;

/**
 * "Dodaj do produktu" from the customer profile (TRE-2110): one client, any
 * product, an access source picked the same way as in the import stepper —
 * with the customer's own active accesses offered as sources to follow.
 */
export const AddCustomerToProductModal = ({ customer, customerProducts, open, onClose, onScheduled }: Props) => {
  const { t } = useTranslation(["products", "common"]);
  const [form] = Form.useForm<FormModel>();
  const [submitting, setSubmitting] = useState(false);
  const [scheduled, setScheduled] = useState<{ productId: string; importId: string } | null>(null);
  const { productsListData } = useAppSelector((store) => store.products);
  const { stripeOptionsByKey } = usePaymentGatewayOptions();
  const productsSettingsStatus = useAppSelector((store) => store.productsSettings.status);
  const dispatch = useAppDispatch();

  // The trainee profile drawer embeds this modal outside ProductsRouter, which
  // is what normally subscribes to the settings holding the Stripe accounts.
  useEffect(() => {
    if (!productsSettingsStatus) {
      dispatch(productsSettingsActions.subscribeToProductsSettings());
    }
  }, [productsSettingsStatus, dispatch]);

  // Products the customer already holds are not offered — a duplicate is a
  // decision for the import, not for a one-off grant — and the rest is grouped
  // by product type so a bundle add-on is found among add-ons, not in a flat list.
  const productOptions = useMemo(() => {
    const activeProductIds = new Set(
      customerProducts.filter((row) => row.status === "active").map((row) => row.productId),
    );
    const available = productsListData.filter(
      (product) => product.status === ProductStatus.ACTIVE && !activeProductIds.has(product.id),
    );

    // Same-named products often live on different Stripe accounts, so each type
    // is split per account like the "Konta Stripe" view of the products list.
    const accountKeys = [
      ...new Set(available.map((product) => getProductStripeAccountKey(product, stripeOptionsByKey))),
    ].sort((a, b) => Number(b === DEFAULT_STRIPE_ACCOUNT_KEY) - Number(a === DEFAULT_STRIPE_ACCOUNT_KEY));
    const showAccount = accountKeys.length > 1;

    return PRODUCT_TYPE_GROUPS.flatMap(({ type, key }) =>
      accountKeys.map((accountKey) => {
        const typeLabel = t(`customersTab.details.addToProduct.typeGroup.${key}`);

        return {
          label: showAccount ? `${typeLabel} - ${getStripeAccountLabel(accountKey, stripeOptionsByKey)}` : typeLabel,
          options: available
            .filter(
              (product) =>
                product.type === type && getProductStripeAccountKey(product, stripeOptionsByKey) === accountKey,
            )
            .map((product) => ({ label: product.name, value: product.id })),
        };
      }),
    ).filter((group) => group.options.length > 0);
  }, [productsListData, customerProducts, stripeOptionsByKey, t]);

  const selectedProductId = Form.useWatch("productId", form);
  const selectedProduct = useMemo(
    () => productsListData.find((item) => item.id === selectedProductId) ?? null,
    [productsListData, selectedProductId],
  );
  const isFree = selectedProduct?.chargeType === ProductChargeType.FREE;
  const activeSources = useMemo(
    () => customerProducts.filter((row) => row.status === "active" && row.productId !== selectedProductId),
    [customerProducts, selectedProductId],
  );

  const accessSourceOptions = useMemo(
    () => [
      {
        label: t("import.accessSource.groupLinked"),
        options: activeSources.map((row) => {
          const endsAt = getLinkedSourceAccessEnd(row);

          return {
            label: `${row.product?.name ?? row.productId}${
              endsAt
                ? ` (${t("customersTab.details.addToProduct.until", { date: dayjs.unix(endsAt).format("DD.MM.YYYY") })})`
                : ""
            }`,
            value: encodeAccessSource({ kind: "linked", sourceProductId: row.productId }),
          };
        }),
      },
      {
        label: t("import.accessSource.groupPrice"),
        options: (selectedProduct?.prices ?? [])
          .filter((price): price is ProductPrice => price.active)
          .map((price) => ({
            label: priceLabel(price, t),
            value: encodeAccessSource({ kind: "price", priceId: price.id }),
          })),
      },
      {
        label: t("import.accessSource.groupOther"),
        options: [{ label: t("import.accessSource.manual"), value: encodeAccessSource({ kind: "manual" }) }],
      },
    ],
    [activeSources, selectedProduct, t],
  );

  const encodedSource = Form.useWatch("accessSource", form);
  const source = useMemo(() => decodeAccessSource(encodedSource), [encodedSource]);
  const linkedRow =
    source?.kind === "linked" ? activeSources.find((row) => row.productId === source.sourceProductId) : null;
  const linkedEnd = linkedRow ? getLinkedSourceAccessEnd(linkedRow) : null;

  useEffect(() => {
    if (!open) {
      setScheduled(null);
      form.resetFields();
    }
  }, [open, form]);

  useEffect(() => {
    // A new product means new prices — the previous source no longer applies.
    form.setFieldsValue({ accessSource: undefined, accessTo: null });
  }, [selectedProductId, form]);

  const { data: summary } = useProductImport(scheduled?.productId ?? "", scheduled?.importId ?? null);
  const finished = summary?.aggregation.status === ClientImportAggregation.Finished;

  // Once per import: the parent's inline callback changes identity on every
  // re-render its own invalidations cause, while `finished` stays true.
  const notifiedImportId = useRef<string | null>(null);

  useEffect(() => {
    if (finished && scheduled && notifiedImportId.current !== scheduled.importId) {
      notifiedImportId.current = scheduled.importId;
      onScheduled?.();
    }
  }, [finished, scheduled, onScheduled]);

  const submit = async (values: FormModel) => {
    if (!customer) {
      return;
    }

    const decoded = isFree ? ({ kind: "manual", accessTo: null } as const) : decodeAccessSource(values.accessSource);

    if (!decoded) {
      return;
    }

    const accessSource: ImportAccessSource =
      decoded.kind === "linked"
        ? { kind: "linked", sourceProductId: decoded.sourceProductId, followSource: values.followSource ?? true }
        : decoded.kind === "price"
          ? { kind: "price", priceId: decoded.priceId, accessTo: values.accessTo?.toISOString() ?? null }
          : { kind: "manual", accessTo: values.accessTo?.toISOString() ?? null };

    const payload: GrantProductAccessRequest = {
      email: customer.email,
      firstName: customer.firstName ?? "",
      lastName: customer.lastName ?? "",
      phoneNumber: customer.phoneNumber,
      accessSource,
      duplicatePolicy: ImportDuplicatePolicy.Skip,
      sendEmailAboutAccountCreation: values.sendEmailAboutAccountCreation ?? false,
    };

    setSubmitting(true);

    try {
      const { data } = await productApi.post<GrantProductAccessResponse>(
        `/products/${values.productId}/clients/grant`,
        payload,
      );
      setScheduled({ productId: values.productId, importId: data.importId });
    } catch (error) {
      void message.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t("customersTab.details.addToProduct.title")}
      open={open}
      onCancel={onClose}
      destroyOnClose
      footer={
        scheduled ? (
          <Space>
            <Link to={`/products/${scheduled.productId}/clients/imports/${scheduled.importId}`} onClick={onClose}>
              <Button>{t("import.summary.openDetails")}</Button>
            </Link>
            <Button type="primary" onClick={onClose}>
              {t("common:button.close")}
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={onClose}>{t("common:button.cancel")}</Button>
            <Button type="primary" loading={submitting} onClick={() => form.submit()}>
              {t("customersTab.details.addToProduct.submit")}
            </Button>
          </Space>
        )
      }
    >
      {scheduled ? (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Typography.Text strong>
            {finished ? t("import.summary.finishedTitle") : t("import.summary.inProgressTitle")}
          </Typography.Text>
          {summary ? <ImportSummaryCounters aggregation={summary.aggregation} /> : null}
          {finished && summary?.clients[0]?.status === "skipped_duplicate" ? (
            <Alert type="info" showIcon message={t("customersTab.details.addToProduct.skippedDuplicate")} />
          ) : null}
          {finished && summary?.clients[0]?.status === "failed" ? (
            <Alert
              type="error"
              showIcon
              message={t("customersTab.details.addToProduct.failed")}
              description={summary.clients[0]?.errorMessage}
            />
          ) : null}
        </Space>
      ) : (
        <Form<FormModel>
          form={form}
          layout="vertical"
          onFinish={(values) => void submit(values)}
          initialValues={{
            followSource: true,
            sendEmailAboutAccountCreation: false,
          }}
        >
          <Typography.Paragraph type="secondary">{customer?.email}</Typography.Paragraph>
          <Form.Item
            name="productId"
            label={t("customersTab.details.addToProduct.product")}
            rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
          >
            <Select showSearch optionFilterProp="label" options={productOptions} />
          </Form.Item>

          {selectedProduct && !isFree ? (
            <Form.Item
              name="accessSource"
              label={t("import.accessSource.label")}
              tooltip={t<string>("import.accessSource.tooltip")}
              rules={[{ required: true, message: t<string>("common:validationErrors.fieldIsRequired") }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                placeholder={t("import.accessSource.placeholder")}
                options={accessSourceOptions}
                popupMatchSelectWidth={false}
              />
            </Form.Item>
          ) : null}

          {source?.kind === "linked" ? (
            <>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message={
                  linkedEnd
                    ? t("customersTab.details.addToProduct.linkedEnd", {
                        date: dayjs.unix(linkedEnd).format("DD.MM.YYYY"),
                      })
                    : t("customersTab.details.addToProduct.linkedUnlimited")
                }
              />
              <Form.Item
                name="followSource"
                label={t("import.accessSource.followSource")}
                tooltip={t<string>("import.accessSource.followSourceHint")}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </>
          ) : null}

          {source && source.kind !== "linked" ? (
            <Form.Item
              name="accessTo"
              label={t("import.headerForm.accessTo")}
              extra={source.kind === "price" ? t("customersTab.details.addToProduct.accessToFromPrice") : undefined}
            >
              <DatePicker
                format="DD.MM.YYYY"
                disabledDate={(current) => current && current < dayjs().endOf("day")}
                placeholder={t<string>(
                  source.kind === "price"
                    ? "customersTab.details.addToProduct.fromPrice"
                    : "import.accessSource.unlimited",
                )}
                style={{ width: "100%" }}
              />
            </Form.Item>
          ) : null}

          <Form.Item
            name="sendEmailAboutAccountCreation"
            label={t("customersTab.details.addToProduct.sendEmail")}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};
